// External imports
import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useStateMachine } from "little-state-machine";

// Internal imports
import "./Files.scss";
import Card from "components/molecules/Card/Card";
import Button from "components/atoms/Button/Button";
import CardNew from "components/molecules/Card/CardNew";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { getBillOfLading, getAttachments, exportQuoteRequest, uploadFile, createTrackingUpdate } from "api";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import { ReactComponent as FileIcon } from "assets/images/icons/File.svg";
import { updateBackgroundLoadingState } from "store/store";
import { useQueryClient } from "@tanstack/react-query";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { extractAllLinks, hasLink } from "helpers/urlHelpers";
import UpdatesDropzone from "../Chat/components/UpdatesDropzone/UpdatesDropzone";

const Files = function ({
  trackingUpdates,
  isLoading,
  openDeleteMessageDialog,
  setSelectedMessageId,
  setSelectedMessageUrl,
  shipmentId,
  activeTab,
  setFileCount,
  isOffPlatform,
}) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const queryClient = useQueryClient();
  const { email, permissions: permissionData, user_type } = JSON.parse(permissions);
  const isShipper = user_type === "shipper";

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [bucketAttachments, setBucketAttachments] = useState([]);
  const [isBolLoading, setIsBolLoading] = useState(false);
  const [isShipmentDetailsLoading, setIsShipmentDetailsLoading] = useState(false);
  const [shipmentDetailsPdfUrl, setShipmentDetailsPdfUrl] = useState("");
  const [isAttachmentHovered, setIsAttachmentHovered] = useState(-1);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const trackingMessageFiles = useMemo(() => {
    if (!trackingUpdates?.length) return;
    const messagesWithUrl = trackingUpdates.filter((item) => item.message && hasLink(item.message));
    return messagesWithUrl.map((data) => ({
      url: extractAllLinks(data.message)?.[0],
      name: data.message.split(":")?.[0] || "File",
    }));
  }, [trackingUpdates]);

  useEffect(() => {
    getUploadedFilesFromMessages(trackingUpdates);
  }, [trackingUpdates]);

  // Set the file count, +2 for Shipment Details and Bill of Lading, which are always present
  useEffect(
    () =>
      setFileCount(
        uploadedFiles.length + bucketAttachments.length + (trackingMessageFiles?.length || 0) + (isOffPlatform ? 0 : 2),
      ),
    [uploadedFiles, bucketAttachments, trackingMessageFiles],
  );

  useEffect(() => {
    if (permissionData?.quoting?.view_quote_requests) {
      getFileAttachments();
    }
  }, [permissionData?.quoting?.view_quote_requests]);

  const getUploadedFilesFromMessages = (messages) => {
    if (!messages) return [];
    const filteredMessages = messages.filter(
      (message) => (message.type === "upload" || message.type === "delivered") && !message.attachment_deleted,
    );
    const uploadedFiles = [];

    filteredMessages.forEach((message) => {
      const { tracking_key, author_email } = message;
      const messageData = message.data;
      const attachmentUrls = messageData.filter((dataItem) => dataItem.key === "attachment_url");

      attachmentUrls.forEach((item) => {
        const attachmentUrl = item.value;
        const attachmentNameSplit = attachmentUrl.split("/");
        const attachmentName = attachmentNameSplit[attachmentNameSplit.length - 1];

        const file = {
          fileName: attachmentName,
          href: attachmentUrl,
          canDelete: author_email === email,
          messageId: tracking_key,
        };

        uploadedFiles.push(file);
      });
    });

    setUploadedFiles(uploadedFiles);
  };

  const onBillOfLadingClick = () => {
    setIsBolLoading(true);
    getBillOfLading(shipmentId)
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        window.open(link.href, "_blank");
      })
      .catch((err) => errorToast(err, "Could not retrieve bill of lading. Please try again later."))
      .finally(() => setIsBolLoading(false));
  };

  const getFileAttachments = () => {
    function formatBucketData(data) {
      return data.map((item) => ({
        id: item?.file?.id || item?.id,
        url: item?.file?.url || item?.url,
        name: item?.file?.name || item?.name,
      }));
    }

    getAttachments(shipmentId)
      .then((res) => setBucketAttachments(formatBucketData(res.data.bucket_attachments.concat(res.data.other_attachments))))
      .catch((err) => errorToast(err));
  };

  const handleMouseHover = (index) => {
    setIsAttachmentHovered(index);
  };

  const handleMouseLeave = () => {
    setIsAttachmentHovered(-1);
  };

  /**
   * Retrieve the shipment details PDF from the API and open it in a new tab
   */
  const onShipmentDetailsClick = () => {
    // Track the click

    // If the shipment details PDF has already been retrieved, open it in  a new tab
    if (shipmentDetailsPdfUrl) {
      window.open(shipmentDetailsPdfUrl, "_blank");
      return;
    }

    // Otherwise, retrieve the shipment details PDF from the API
    setIsShipmentDetailsLoading(true);
    exportQuoteRequest(shipmentId, "print-shipment-details")
      .then((res) => {
        // Create a blob from the response
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Save the URL to state
        setShipmentDetailsPdfUrl(url);

        // Open the PDF in a new  tab
        window.open(url, "_blank");
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsShipmentDetailsLoading(false));
  };

  /** This function uploads the selected file to the /common/files endpoint
   *  and stores the response in the rhf value "uploadedFile"
   * @param {File} file - The file to be uploaded
   */
  const uploadSelectedFile = (file) => {
    setIsUploadingFile(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);
    uploadFile(formData)
      .then((res) => {
        if (res.status === 201) {
          const fileData = {
            url: res.data.url,
            filename: res.data.name,
            size: file.size,
          };
          // Create the new message object
          const newPostData = {
            type: "upload",
            attachments: [fileData],
          };
          // Submit the message
          createTrackingUpdate(shipmentId, newPostData)
            .then(() => {
              // Refetch the tracking updates
              setTimeout(() => {
                actions.updateBackgroundLoadingState(true);
                queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentGeoDataDetails(shipmentId) });
              }, [1000]);
            })
            .catch((err) =>
              errorToast(
                err,
                intl.formatMessage({
                  id: "tracking.couldNotUploadFile",
                  defaultMessage: "Could not upload file. Please try again later.",
                }),
              ),
            );
        }
      })
      .catch((error) => {
        errorToast(error);
      })
      .finally(() =>
        setTimeout(() => {
          setIsUploadingFile(false);
        }, [1000]),
      );
  };

  if (activeTab !== "files") return null;
  return (
    <CardNew isLoading={isLoading} className="mr-9 ml-9">
      <Card.Body style={{ padding: 0 }} className="files-container font-b6">
        {!isOffPlatform && (
          <div className="d-flex mb-1 align-items-center">
            <FileIcon className="mr-2" />
            <Button className="p-0 font-b6" style={{ fontSize: 10 }} variant="link" onClick={onShipmentDetailsClick}>
              <FormattedMessage id="general.shipmentDetails" defaultMessage="Shipment Details" />
            </Button>
            {isShipmentDetailsLoading && (
              <FontAwesomeIcon icon={faCircleNotch} className="text-secondary font-16 ml-2" spin />
            )}
          </div>
        )}
        {!isOffPlatform && (
          <div className="d-flex mb-1 align-items-center">
            <FileIcon className="mr-2" />
            <Button
              className="p-0 font-b6"
              style={{ fontSize: 10 }}
              variant="link"
              label={intl.formatMessage({ id: "tracking.billOfLading", defaultMessage: "Shipment Summary" })}
              onClick={onBillOfLadingClick}
            />
            {isBolLoading && <FontAwesomeIcon icon={faCircleNotch} className="text-secondary font-16 ml-2" spin />}
          </div>
        )}
        {bucketAttachments.map((attachment) => (
          <div className="d-flex align-items-center mb-1">
            <FileIcon className="mr-2" />
            <a target="_blank" style={{ fontSize: 10 }} href={attachment?.url} rel="noopener noreferrer">
              {attachment?.name}
            </a>
          </div>
        ))}
        {!!trackingMessageFiles?.length &&
          trackingMessageFiles.map((data) => (
            <div className="d-flex align-items-center mb-1">
              <FileIcon className="mr-2" />
              <a target="_blank" style={{ fontSize: 10 }} href={data?.url} rel="noopener noreferrer">
                {data?.name}
              </a>
            </div>
          ))}
        {uploadedFiles.length > 0 &&
          uploadedFiles.map(({ fileName, href, canDelete, messageId }, index) => (
            <div
              key={fileName + Math.random()}
              onMouseEnter={() => handleMouseHover(index)}
              onMouseLeave={handleMouseLeave}
              className="d-flex align-items-center mb-1 font-b6"
            >
              <FileIcon className="mr-2" style={{ flexShrink: 0 }} />
              <a
                className="file-name"
                target="_blank"
                href={href}
                rel="noreferrer"
              >
                {fileName}
              </a>
              {isAttachmentHovered === index && canDelete && (
                <CircleCross
                  stroke="black"
                  onClick={() => {
                    setSelectedMessageId(messageId);
                    setSelectedMessageUrl(href);
                    openDeleteMessageDialog();
                  }}
                  style={{ marginLeft: 6, cursor: "pointer" }}
                />
              )}
            </div>
          ))}
      </Card.Body>
      <Card.Footer>
        {!isShipper && (
          <UpdatesDropzone uploadSelectedFile={uploadSelectedFile} isLoading={isUploadingFile} className="mt-18" />
        )}
      </Card.Footer>
    </CardNew>
  );
};

Files.propTypes = {
  trackingUpdates: PropTypes.instanceOf(Array).isRequired,
  isLoading: PropTypes.bool.isRequired,
  openDeleteMessageDialog: PropTypes.func.isRequired,
  setSelectedMessageId: PropTypes.func.isRequired,
  setSelectedMessageUrl: PropTypes.func.isRequired,
  shipmentId: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
  setFileCount: PropTypes.func.isRequired,
  isOffPlatform: PropTypes.bool.isRequired,
};

export default Files;
